(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const initFaq = (root = document) => {
    root.querySelectorAll?.('[data-v37-faq]:not([data-v37-ready])').forEach((faq) => {
      faq.dataset.v37Ready = 'true';
      const items = [...faq.querySelectorAll('[data-v37-faq-item]')];

      // V172: keep FAQ interaction clean and single-step.
      // The previous burst class created a second visual animation on toggle,
      // so the accordion now relies only on the existing open/close transition.
      items.forEach((item) => {
        item.classList.remove('is-v37-bursting');
      });
    });
  };

  const initPopup = () => {
    const popup = document.querySelector('[data-smoothco-popup]');
    if (!popup || popup.dataset.v37PopupReady === 'true') return;
    popup.dataset.v37PopupReady = 'true';

    const dialog = popup.querySelector('.smoothco-popup__dialog');
    const closeButtons = [...popup.querySelectorAll('[data-popup-close]')];
    const delay = Math.max(0, Number(popup.dataset.popupDelay || 10000));
    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    let previousFocus = null;
    let openingTimer = null;
    let wasClosedOnThisDocument = false;
    const popupSessionKey = 'smoothcoPopupSeen';
    const popupPendingSuccessKey = 'smoothcoPopupPendingSuccess';

    const isOpen = () => popup.classList.contains('is-open');
    const hasSeenThisSession = () => {
      try {
        return window.sessionStorage.getItem(popupSessionKey) === 'true';
      } catch (error) {
        return false;
      }
    };
    const markSeenThisSession = () => {
      try {
        window.sessionStorage.setItem(popupSessionKey, 'true');
      } catch (error) {
        // Ignore storage failures and keep the popup functional.
      }
    };
    const hasPendingSuccess = () => {
      try {
        return window.sessionStorage.getItem(popupPendingSuccessKey) === 'true';
      } catch (error) {
        return false;
      }
    };
    const markPendingSuccess = () => {
      try {
        window.sessionStorage.setItem(popupPendingSuccessKey, 'true');
      } catch (error) {
        // Ignore storage failures.
      }
    };
    const clearPendingSuccess = () => {
      try {
        window.sessionStorage.removeItem(popupPendingSuccessKey);
      } catch (error) {
        // Ignore storage failures.
      }
    };

    const open = ({ force = false } = {}) => {
      if (isOpen()) return;
      if ((wasClosedOnThisDocument || hasSeenThisSession()) && !force) return;
      if (!force) markSeenThisSession();

      previousFocus = document.activeElement;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('smoothco-popup-open');

      window.requestAnimationFrame(() => {
        const email = popup.querySelector('input[type="email"]');
        (email || dialog)?.focus({ preventScroll: true });
      });
    };

    const close = () => {
      if (!isOpen()) return;
      wasClosedOnThisDocument = true;
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('smoothco-popup-open');

      if (previousFocus instanceof HTMLElement) {
        previousFocus.focus({ preventScroll: true });
      }
    };

    const scheduleOpen = ({ force = false, customDelay = delay } = {}) => {
      if (!force && hasSeenThisSession()) return;
      window.clearTimeout(openingTimer);

      const queueOpen = () => {
        openingTimer = window.setTimeout(() => open({ force }), Math.max(0, customDelay));
      };

      if (document.readyState !== "complete") {
        window.addEventListener("load", queueOpen, { once: true });
        return;
      }

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(queueOpen, { timeout: 1200 });
        return;
      }

      queueOpen();
    };

    closeButtons.forEach((button) => button.addEventListener('click', close));

    document.addEventListener('keydown', (event) => {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...popup.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.querySelectorAll('.js-open-popup').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        wasClosedOnThisDocument = false;
        open({ force: true });
      });
    });

    const popupForm = popup.querySelector('#SmoothcoGlobalPopupForm');
    const popupCapture = popup.querySelector('[data-popup-capture]');
    const popupSuccessTemplate = popup.querySelector('[data-popup-success-template]');
    const query = new URLSearchParams(window.location.search);
    const urlSignalsSuccess = ['customer_posted', 'contact_posted', 'newsletter_posted'].some((key) => query.get(key) === 'true');
    const hashSignalsSuccess = /contact_form|newsletter/i.test(window.location.hash || '');
    const revealPendingSuccessState = () => {
      if (!popupCapture || !popupSuccessTemplate) return false;
      popupCapture.hidden = true;
      popupSuccessTemplate.hidden = false;
      popupSuccessTemplate.setAttribute('data-popup-success', 'true');
      return true;
    };

    popupForm?.addEventListener('submit', () => {
      markPendingSuccess();
    });

    // After Shopify accepts the email, always reveal the success/code state once.
    // This intentionally bypasses the once-per-session gate so customers always see the confirmed first-order saving state.
    const hasSuccessfulSubmission = Boolean(popup.querySelector('[data-popup-success]'));
    const pendingSuccess = hasPendingSuccess() || urlSignalsSuccess || hashSignalsSuccess;
    if (hasSuccessfulSubmission || pendingSuccess) {
      if (!hasSuccessfulSubmission && pendingSuccess) {
        revealPendingSuccessState();
      }
      clearPendingSuccess();
      markSeenThisSession();
      open({ force: true });
    } else {
      // Normal acquisition popup: auto-open only once per browser session.
      scheduleOpen();
    }

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      wasClosedOnThisDocument = false;
      scheduleOpen({ customDelay: 280 });
    });

    document.addEventListener('shopify:section:load', () => {
      if (!window.Shopify?.designMode) return;
      wasClosedOnThisDocument = false;
      scheduleOpen({ force: true, customDelay: 120 });
    });

    // Prevent the Shopify Forms app popup from stacking over this custom global popup.
    const hideLegacyPopup = (root = document) => {
      const selectors = [
        '[data-shopify-forms]',
        '[data-shopify-forms-popup]',
        '[id*="shopify-forms" i]',
        '[class*="shopify-forms" i]',
        'iframe[src*="shopify-forms" i]'
      ];

      root.querySelectorAll?.(selectors.join(',')).forEach((node) => {
        if (node.closest?.('#SmoothcoEmailPopup')) return;
        const popupLike =
          node.matches?.('[role="dialog"], [aria-modal="true"], iframe') ||
          node.querySelector?.('[role="dialog"], [aria-modal="true"]') ||
          getComputedStyle(node).position === 'fixed';
        if (!popupLike) return;
        node.setAttribute('aria-hidden', 'true');
        node.style.setProperty('display', 'none', 'important');
      });
    };

    hideLegacyPopup();
    const legacyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          hideLegacyPopup(node);
        });
      });
    });

    legacyObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  ready(() => {
    initFaq();
    initPopup();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initFaq(event.target);
  });
})();

// V180 product FAQ default closed
document.querySelectorAll('.smoothco-product-faq-v179 details').forEach((item) => { item.open = false; });
